import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Camera } from '@babylonjs/core/Cameras/camera';
import { Epsilon, Vector2, Vector3 } from '@babylonjs/core/Maths';
import { Scene } from '@babylonjs/core/scene';
import { KbArcRotateCameraMouseWheelInput } from './kb-arc-rotate-camera-wheel-input';

/** Arc Rotate Camera (orbit camera) modified to offset the screen position on pan and keep the orbit target in the same position */
export class KbArcRotateCamera extends ArcRotateCamera {
    constructor(
        name: string,
        alpha: number,
        beta: number,
        radius: number,
        target: Vector3,
        private scene: Scene,
        setActiveOnSceneIfNoneActive?: boolean
    ) {
        super(name, alpha, beta, radius, target, scene, setActiveOnSceneIfNoneActive);

        //replace the wheel input with our own
        this.inputs.removeByType('ArcRotateCameraMouseWheelInput');
        this.inputs.add(new KbArcRotateCameraMouseWheelInput());
        this.updateClient();
    }
    inertialOffsetPanningX = 0;
    inertialOffsetPanningY = 0;
    inertialOrthoZoom = 0;
    clientInfo = {
        mouseX: 0,
        mouseY: 0,
        screenLeft: 0,
        screenTop: 0,
        screenWidth: 0,
        screenHeight: 0,
    };
    lockRotation = false;
    lockPanning = false;
    lockZoom = false;
    limitMinimumTargetDistance = false;

    private disposeListeners: (() => void) | undefined;
    private _orthoWidth = 0;
    get orthoWidth() {
        return this._orthoWidth;
    }
    set orthoWidth(val) {
        this._orthoWidth = this.clampOrthoWidth(val);
        this.updateOrthographicPosition();
    }

    orthoFit(sizeX: number, sizeY: number) {
        if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
            const ratio = this.clientInfo.screenHeight / this.clientInfo.screenWidth;
            if (sizeX * ratio < sizeY) {
                this.orthoWidth = sizeY / ratio;
            } else {
                this.orthoWidth = sizeX;
            }
        }
    }

    update() {
        if (this.scene.activeCamera !== this) {
            return;
        }
        let startZoomLevel: number | undefined;
        if (!this.targetScreenOffset) {
            this.targetScreenOffset = new Vector2(0, 0);
        }

        if (this.lockRotation && (this.inertialAlphaOffset !== 0 || this.inertialBetaOffset !== 0)) {
            this.inertialPanningX = this.inertialAlphaOffset;
            this.inertialPanningY = -this.inertialBetaOffset;
            this.inertialAlphaOffset = this.inertialBetaOffset = 0;
        }

        if (this.inertialPanningX !== 0 || this.inertialPanningY !== 0) {
            if (!this.lockPanning) {
                this.inertialOffsetPanningX = this.inertialPanningX;
                this.inertialOffsetPanningY = this.inertialPanningY;
            }
            this.inertialPanningX = 0;
            this.inertialPanningY = 0;
        }

        if (this.lockZoom && this.inertialRadiusOffset !== 0) {
            this.inertialRadiusOffset = 0;
        }

        if (this.inertialRadiusOffset !== 0) {
            if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                this.inertialOrthoZoom = this.inertialRadiusOffset;
                this.inertialRadiusOffset = 0;
            } else {
                startZoomLevel = this.radius;
                //this.inertialRadiusOffset = 0;  //override so we have full control over the zoom
                //if (this.inertialRadiusOffset < .001) this.inertialRadiusOffset = .001;
            }
        }

        if (this.inertialOrthoZoom !== 0) {
            startZoomLevel = this.orthoWidth;
        }

        super.update();

        if (this.inertialOffsetPanningX !== 0 || this.inertialOffsetPanningY !== 0) {
            let offset: Vector2;
            if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                offset = new Vector2(
                    this.inertialOffsetPanningX * this.orthoWidth,
                    this.inertialOffsetPanningY * this.orthoWidth
                );
            } else {
                offset = new Vector2(
                    this.inertialOffsetPanningX * this.radius,
                    this.inertialOffsetPanningY * this.radius
                );
            }
            this.targetScreenOffset.subtractInPlace(offset);

            this.inertialOffsetPanningX *= this.panningInertia;
            this.inertialOffsetPanningY *= this.panningInertia;
        }

        if (this.inertialOrthoZoom !== 0) {
            this.orthoWidth = this.orthoWidth - this.inertialOrthoZoom * this.orthoWidth;

            this.inertialOrthoZoom *= this.panningInertia;
        }

        if (Math.abs(this.inertialOffsetPanningX) < this.speed * Epsilon) {
            this.inertialOffsetPanningX = 0;
        }
        if (Math.abs(this.inertialOffsetPanningY) < this.speed * Epsilon) {
            this.inertialOffsetPanningY = 0;
        }
        if (Math.abs(this.inertialOrthoZoom) < this.speed * Epsilon) {
            this.inertialOrthoZoom = 0;
        }

        // When zooming, adjust the camera pan so you zoom into your mouse position, as long as pan is enabled
        if (!this.lockPanning && startZoomLevel !== undefined) {
            if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
                const zoomChange = (startZoomLevel - this.orthoWidth) / 2;

                this.targetScreenOffset.x -= zoomChange * this.clientInfo.mouseX;
                this.targetScreenOffset.y += zoomChange * this.clientInfo.mouseY;
            } else {
                const gamma = Math.tan(this.fov / 2);
                const zoomChange = startZoomLevel - this.radius;

                const shiftX = zoomChange * gamma * this.clientInfo.mouseX;
                const shiftY = zoomChange * gamma * this.clientInfo.mouseY;
                this.targetScreenOffset.x -= shiftX;
                this.targetScreenOffset.y += shiftY;
            }
        }
    }

    updateClient() {
        const canvas = this.getEngine().getRenderingCanvas();
        if (!canvas) {
            return;
        }

        const clientRect = canvas.getBoundingClientRect();
        this.clientInfo.screenWidth = clientRect.width;
        this.clientInfo.screenHeight = clientRect.height;
        this.clientInfo.screenLeft = clientRect.left;
        this.clientInfo.screenTop = clientRect.top;

        if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
            this.updateOrthographicPosition();

            this.panningSensibility = clientRect.width;
        } else {
            const focalLength = clientRect.height / (2 * Math.tan(this.fov / 2));
            this.panningSensibility = focalLength;
        }

        return canvas;
    }

    attachControl(noPreventDefault = false) {
        super.attachControl(noPreventDefault, true);
        if (this.disposeListeners) {
            this.disposeListeners();
        }

        const updateMousePosition = (event: MouseEvent) => {
            this.clientInfo.mouseX =
                ((event.clientX - this.clientInfo.screenLeft) * 2) / this.clientInfo.screenWidth - 1;
            this.clientInfo.mouseY =
                ((event.clientY - this.clientInfo.screenTop) * 2) / this.clientInfo.screenHeight - 1;
        };
        const canvas = this.updateClient();
        const updateClientWrap = () => this.updateClient();

        if (canvas) {
            window.addEventListener('resize', updateClientWrap);
            canvas.addEventListener('mousemove', updateMousePosition);

            this.disposeListeners = () => {
                window.removeEventListener('resize', updateClientWrap);
                canvas.removeEventListener('mousemove', updateMousePosition);
                this.disposeListeners = undefined;
            };
        }
    }

    _checkLimits() {
        super._checkLimits();
        if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
            this.orthoWidth = this.clampOrthoWidth(this.orthoWidth);
        }
    }

    private clampOrthoWidth(val: number) {
        if (this.mode === Camera.ORTHOGRAPHIC_CAMERA) {
            if (this.lowerRadiusLimit != null && val < this.lowerRadiusLimit) {
                val = this.lowerRadiusLimit;
            }
            if (this.upperRadiusLimit != null && val > this.upperRadiusLimit) {
                val = this.upperRadiusLimit;
            }
        }

        return val;
    }

    dispose() {
        super.dispose();
        if (this.disposeListeners) {
            this.disposeListeners();
        }
    }

    private updateOrthographicPosition() {
        if (this.clientInfo) {
            const ratio = this.clientInfo.screenHeight / this.clientInfo.screenWidth;
            this.orthoRight = this.orthoWidth / 2;
            this.orthoLeft = -this.orthoWidth / 2;
            this.orthoTop = (this.orthoWidth * ratio) / 2;
            this.orthoBottom = (-this.orthoWidth * ratio) / 2;
        }
    }
}
